import { Link } from 'react-router-dom';
import { Twitter, Twitch, Youtube, Instagram, Facebook, Github, Globe, type LucideIcon } from 'lucide-react';
import type { OrgPublicPageResponse } from '../lib/api';
import { CopyLinkButton } from './CopyLinkButton';
import { accentVarsFor } from './AccentInjector';
import { clanLabelFrom, displayClanName } from '../lib/displayName';
import '../pages/CompHome.css';

// ──────────────────────────────────────────────────────────────────────────────
// OrgPublicPageView — presentational render of the org public page.
//
// Pure function of `data` (OrgPublicPageResponse) — no fetching, no document-level
// side effects. Used by both:
//   - OrgHome (the real public route, /org/:slug) — real fetched data.
//   - OrgPublicPageEditor's inline Preview mode — live, unsaved form state.
//   - MyOrg (the in-shell "my org" info tab) — same real fetched data as OrgHome.
//
// IMPORTANT: this component must NOT touch document.documentElement (accent
// vars etc). Doing so would hijack the whole app's accent whenever it renders
// inside the editor's live preview. Accent application at the whole-app level
// stays in OrgHome only. What THIS file does instead: derive the org's accent
// palette from `data.org.accentColor` and apply it as an inline CSS custom
// property scoped to this component's own root node (the same "scoped colour
// for a specific surface" technique AccentInjector.tsx documents for things
// like another player's card) — never document.documentElement. That keeps
// the component pure, and — as a bonus — means the editor's live Preview now
// tints correctly too, which OrgHome's page-level effect alone couldn't give it.
//
// Design system: this reuses CompHome.css's `.comp-home` scope (the shared
// GG Anvil public-page look) verbatim — same topbar/hero/plate/pill/btn/grid classes
// the comp public page uses, plus a small set of org-specific additions at the
// bottom of that file (org-card, clan-chip, standings-table, md-lite, etc.)
// for shapes CompHome.css had no equivalent for.
// ──────────────────────────────────────────────────────────────────────────────

export const PLACE_MEDALS: Record<number, string> = { 1: '🥇', 2: '🥈', 3: '🥉' };

// ── Small helpers ─────────────────────────────────────────────────────────────

// StatCell — kept verbatim (Tailwind-styled) for ClanPublicPageView, which
// imports it for its own (unrelated, un-restyled) stats bar. Not used by this
// file's own render anymore — the org page folds these numbers into the hero
// pills + "At a glance" panel instead — but the export must stay stable.
export function StatCell({ label, value }: { label: string; value: number }) {
  return (
    <div className="text-center">
      <p className="text-3xl font-bold text-white">{value.toLocaleString()}</p>
      <p className="mt-1 text-xs uppercase tracking-wide text-gray-500">{label}</p>
    </div>
  );
}

// Strip the DDB pk prefix that leaks into clan names stored in match rows.
export function stripPrefix(name: string) {
  return name.replace(/^CLAN#/, '').replace(/^COMP#/, '');
}

// First paragraph of a blank-line-delimited text blob — used as the hero lead
// when the org has no tagline but does have an about intro.
function firstParagraph(text: string | undefined): string | undefined {
  if (!text) return undefined;
  const [first] = text.split(/\n{2,}/).map(p => p.trim()).filter(Boolean);
  return first;
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

// Markdown-lite renderer for the Code of Conduct section. Line-based: # / ##
// headings, - or * bullets, "1." numbered items, blank-line paragraph breaks.
// Built as React elements from plain text — no HTML parsing, so no injection
// surface. Renders bare h3/h4/p/ul/ol/li — styling comes entirely from the
// `.md-lite` cascade in CompHome.css, no per-block classNames needed.
export function MarkdownLite({ text }: { text: string }) {
  const blocks: React.ReactNode[] = [];
  let list: { ordered: boolean; items: string[] } | null = null;

  const flushList = (key: number) => {
    if (!list) return;
    const items = list.items.map((item, i) => <li key={i}>{item}</li>);
    blocks.push(
      list.ordered
        ? <ol key={`l${key}`}>{items}</ol>
        : <ul key={`l${key}`}>{items}</ul>,
    );
    list = null;
  };

  text.split('\n').forEach((rawLine, idx) => {
    const line = rawLine.trim();
    const bullet  = /^[-*]\s+(.*)$/.exec(line);
    const numbered = /^\d+[.)]\s+(.*)$/.exec(line);
    if (bullet || numbered) {
      const ordered = !!numbered;
      const item = (bullet ?? numbered)![1];
      if (list && list.ordered !== ordered) flushList(idx);
      if (!list) list = { ordered, items: [] };
      list.items.push(item);
      return;
    }
    flushList(idx);
    if (!line) return;
    if (line.startsWith('## ')) {
      blocks.push(<h4 key={idx}>{line.slice(3)}</h4>);
    } else if (line.startsWith('# ')) {
      blocks.push(<h3 key={idx}>{line.slice(2)}</h3>);
    } else {
      blocks.push(<p key={idx}>{line}</p>);
    }
  });
  flushList(-1);

  return <div className="md-lite">{blocks}</div>;
}

// Map a social link's label or URL host to a lucide icon. lucide-react has no
// TikTok/X-brand glyph, so anything unrecognised falls back to Globe.
export function socialIcon(label: string, url: string): LucideIcon {
  const l = label.trim().toLowerCase();
  let host = '';
  try {
    host = new URL(url).hostname.toLowerCase().replace(/^www\./, '');
  } catch {
    // Not a parseable URL — fall through to label-only matching.
  }
  const hay = `${l} ${host}`;
  if (hay.includes('twitter') || hay.includes('x.com') || l === 'x') return Twitter;
  if (hay.includes('twitch')) return Twitch;
  if (hay.includes('youtube') || l === 'yt') return Youtube;
  if (hay.includes('instagram') || l === 'ig') return Instagram;
  if (hay.includes('facebook') || l === 'fb') return Facebook;
  if (hay.includes('github')) return Github;
  return Globe;
}

// ── OrgPublicPageView ────────────────────────────────────────────────────────

export function OrgPublicPageView({ data }: { data: OrgPublicPageResponse }) {
  const { org, page, comps, stats, history, hallOfChampions, memberClans, featuredComp } = data;

  const hero    = page.hero    ?? {};
  const about   = page.about   ?? {};
  const contact = page.contact ?? {};

  // Page-editor logo wins if explicitly set; otherwise fall back to the org's
  // own uploaded branding logo (OrgBranding.tsx); otherwise no logo at all —
  // same fallback order at all three render spots below (topbar, hero-lockup,
  // footer) so an org that never touched the page editor still shows its logo.
  const orgLogoUrl = hero.logoAssetUrl ?? org.logoUrl ?? undefined;

  const hasAbout           = !!(about.whoWeAre || about.howWeWork);
  const hasFeatured        = !!featuredComp;
  const hasCurrentComps    = comps.length > 0;
  const hasHistory         = history.length > 0;
  const hasHallOfChampions = hallOfChampions.length > 0;
  const hasMemberClans     = (memberClans?.length ?? 0) > 0;
  const hasSeasons         = (page.seasons?.length ?? 0) > 0;
  const hasTestimonials    = (page.testimonials?.length ?? 0) > 0;
  const hasFaq             = (page.faq?.length ?? 0) > 0;
  const hasStaff           = (page.staff?.length ?? 0) > 0;
  const hasPartners        = (page.partners?.length ?? 0) > 0;
  const hasSocials         = (contact.socials?.length ?? 0) > 0;
  const hasContact         = !!(contact.email || contact.note || contact.website);
  const hasRules           = !!page.rules;
  const establishedYear    = org.createdAt ? new Date(org.createdAt).getFullYear() : null;

  const heroEyebrow = establishedYear ? `Organisation · Established ${establishedYear}` : 'Organisation';
  const heroLead     = hero.tagline?.trim() || firstParagraph(about.whoWeAre);
  const hasPills     = hasCurrentComps || hasMemberClans || stats.totalPlayers > 0;

  // "At a glance" facts — real counts only; a field with no data is omitted
  // rather than shown as a fabricated zero/TBD (there's no `region` field on
  // the org data model at all, so that mockup slot is dropped entirely).
  const atAGlanceFacts: { k: string; v: string }[] = [];
  if (stats.totalComps > 0) atAGlanceFacts.push({ k: 'Competitions', v: stats.totalComps.toLocaleString() });
  if (hasMemberClans) atAGlanceFacts.push({ k: 'Clans', v: memberClans!.length.toLocaleString() });
  else if (stats.totalTeams > 0) atAGlanceFacts.push({ k: 'Teams', v: stats.totalTeams.toLocaleString() });
  if (stats.totalPlayers > 0) atAGlanceFacts.push({ k: 'Players', v: stats.totalPlayers.toLocaleString() });
  if (establishedYear) atAGlanceFacts.push({ k: 'Established', v: String(establishedYear) });
  const hasAtAGlance = atAGlanceFacts.length > 0;

  // Scope the org's accent colour to just this subtree via an inline custom
  // property — never document.documentElement (see header comment). Falls
  // back to the platform's own forge orange, applied explicitly here rather
  // than relying on `.comp-home`'s own default (which comp pages fall through
  // to instead — see CompHome.css) — keeps org and comp pages independently
  // overridable.
  const orgAccent500 = accentVarsFor(null, org.accentColor ?? undefined)['--accent-500'];
  const rootStyle = { '--accent': orgAccent500 ?? 'var(--forge-500)' } as React.CSSProperties;

  return (
    <div className="comp-home org-hero" style={rootStyle}>

      {/* ── Top bar ─────────────────────────────────────────────────────── */}
      <header className="topbar">
        <div className="wrap">
          <div className="brand">
            {orgLogoUrl && <img src={orgLogoUrl} alt={`${org.name} logo`} />}
            <div className="name">{org.name}<small>GG Anvil Organisation</small></div>
          </div>
          <Link to="/" className="backlink">← GG&nbsp;Anvil Portal</Link>
        </div>
      </header>

      {/* ── Hero banner ─────────────────────────────────────────────────── */}
      {/* Cover-photo band: the banner image ONLY — no text/logo/pills/CTAs
          overlaid on it (those all live in the identity block below, on the
          solid page background, so nothing collides with whatever the banner
          image itself contains). has-banner keeps CompHome's dark-scrim class
          name (CompHome.css) for the no-JS/no-CSS-loaded fallback, but
          .org-hero overrides that to a bottom-only fade — see CompHome.css. */}
      <section
        className={hero.bannerAssetUrl ? 'hero has-banner' : 'hero'}
        style={hero.bannerAssetUrl ? {
          backgroundImage: `url(${hero.bannerAssetUrl})`,
          backgroundSize: 'cover',
          backgroundPosition: hero.bannerObjectPosition ?? 'center',
        } : undefined}
      />

      {/* ── Identity block ──────────────────────────────────────────────── */}
      {/* Classic "cover + profile" layout: the logo overlaps the banner's
          bottom edge (negative top margin on .org-identity-lockup, pulled
          above the fade via its own z-index — see CompHome.css), then
          eyebrow/name/lead/pills/CTAs/socials stack below it on clean
          background. Plain <div> (not <section>) so it skips the generic
          `.comp-home section{padding:64px 0}` rule and can pull the logo up
          without that padding fighting it. */}
      <div className="org-identity">
        <div className="wrap">
          <div className="org-identity-lockup">
            {orgLogoUrl && (
              <img className="hero-logo-img org-identity-logo" src={orgLogoUrl} alt={`${org.name} logo`} />
            )}
          </div>

          <div className="season-tag">{heroEyebrow}</div>
          <h1>{org.name}</h1>

          {heroLead && <p className="sub">{heroLead}</p>}

          {hasPills && (
            <div className="chips">
              {hasCurrentComps && (
                <span className="pill">
                  <span className="dot"></span>
                  {comps.length} competition{comps.length === 1 ? '' : 's'} live
                </span>
              )}
              {hasMemberClans && (
                <span className="pill">{memberClans!.length} clan{memberClans!.length === 1 ? '' : 's'}</span>
              )}
              {stats.totalPlayers > 0 && (
                <span className="pill">{stats.totalPlayers.toLocaleString()} players</span>
              )}
            </div>
          )}

          <div className="cta-row">
            {hasFeatured && (
              <Link className="btn btn-primary" to={`/?join=${encodeURIComponent(featuredComp!.id)}`}>
                Register now
              </Link>
            )}
            {contact.discordInviteUrl && (
              <a className="btn btn-ghost" href={contact.discordInviteUrl} target="_blank" rel="noopener noreferrer">
                Join our Discord
              </a>
            )}
            {hasRules && <a className="btn btn-ghost" href="#coc">Read the Code of Conduct</a>}
            <CopyLinkButton label="Copy page link" />
          </div>

          {hasSocials && (
            <div className="social-row">
              {contact.socials!.map((s, idx) => {
                const Icon = socialIcon(s.label, s.url);
                return (
                  <a key={idx} href={s.url} target="_blank" rel="noopener noreferrer" aria-label={s.label}>
                    <Icon size={18} strokeWidth={2.25} />
                  </a>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* ── About + At-a-glance ─────────────────────────────────────────── */}
      {(hasAbout || hasAtAGlance) && (
        <section id="about">
          <div className="wrap">
            <div className="eyebrow"><span className="tick"></span>About the organisation</div>
            <h2 className="sec-title">About {org.name}</h2>
            <div
              className={hasAbout && hasAtAGlance ? 'grid g-2' : undefined}
              style={{ marginTop: 22, alignItems: 'start' }}
            >
              {hasAbout && (
                <div>
                  {about.whoWeAre && (
                    <p className="lead" style={{ whiteSpace: 'pre-line' }}>{about.whoWeAre}</p>
                  )}
                  {about.howWeWork && (
                    <p className="lead" style={{ whiteSpace: 'pre-line', marginTop: about.whoWeAre ? 14 : 0 }}>
                      {about.howWeWork}
                    </p>
                  )}
                </div>
              )}
              {hasAtAGlance && (
                <div className="plate pad">
                  <div className="eyebrow" style={{ marginBottom: 14 }}><span className="tick"></span>At a glance</div>
                  <div className="grid g-2" style={{ gap: 14 }}>
                    {atAGlanceFacts.map(f => (
                      <div key={f.k} className="fact"><div className="k">{f.k}</div><div className="v">{f.v}</div></div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* ── Featured competition ────────────────────────────────────────── */}
      {hasFeatured && (
        <section id="featured" style={{ background: 'var(--bg-2)' }}>
          <div className="wrap">
            <div className="eyebrow"><span className="tick"></span>Featured</div>
            <h2 className="sec-title">Featured Competition</h2>
            <div
              className="plate pad"
              style={{ marginTop: 22, display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 20 }}
            >
              <div>
                <div className="display" style={{ fontSize: 24 }}>{featuredComp!.name}</div>
                <div className="mono" style={{ marginTop: 6, fontSize: 12.5, letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--ink-faint)' }}>
                  {featuredComp!.format ?? 'Competition'} · {featuredComp!.status}
                </div>
              </div>
              <Link className="btn btn-primary" to={`/?join=${encodeURIComponent(featuredComp!.id)}`}>Register</Link>
            </div>
          </div>
        </section>
      )}

      {/* ── Current competitions ────────────────────────────────────────── */}
      {hasCurrentComps && (
        <section id="competitions">
          <div className="wrap">
            <div className="eyebrow"><span className="tick"></span>Live &amp; upcoming</div>
            <h2 className="sec-title">Current Competitions</h2>
            <div className="grid g-3" style={{ marginTop: 22 }}>
              {comps.map(comp => (
                <div key={comp.id} className="plate pad org-card">
                  <div className="name">{comp.name}</div>
                  <div className="meta">{comp.format ?? 'Competition'} · {comp.status}</div>
                  <Link className="btn btn-ghost" to={`/?join=${encodeURIComponent(comp.id)}`}>Register →</Link>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── Our teams / clans ───────────────────────────────────────────── */}
      {hasMemberClans && (
        <section id="teams" style={{ background: 'var(--bg-2)' }}>
          <div className="wrap">
            <div className="eyebrow">
              <span className="tick"></span>
              {memberClans!.length} clan{memberClans!.length === 1 ? '' : 's'} strong
            </div>
            <h2 className="sec-title">Our Teams</h2>
            <div className="chips" style={{ marginTop: 22 }}>
              {memberClans!.map(clan => (
                <span key={clan.clanId} className="clan-chip">{displayClanName(clan.name)}</span>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── Past seasons ─────────────────────────────────────────────────── */}
      {hasHistory && (
        <section id="seasons">
          <div className="wrap">
            <div className="eyebrow"><span className="tick"></span>The archive</div>
            <h2 className="sec-title">Past Seasons</h2>
            <div className="grid g-2" style={{ marginTop: 22 }}>
              {history.map(entry => (
                <div key={entry.compId} className="plate pad org-card">
                  <div className="name">{entry.name}</div>
                  <div className="meta">
                    {entry.teamCount} {entry.teamCount === 1 ? 'team' : 'teams'} ·{' '}
                    {entry.playerCount} {entry.playerCount === 1 ? 'player' : 'players'}
                  </div>
                  {entry.podium.length > 0 && (
                    <div className="podium-row">
                      {entry.podium.map(p => (
                        <span key={p.place} className="clan-chip">
                          <span className="medal">{PLACE_MEDALS[p.place] ?? `${p.place}.`}</span>
                          {displayClanName(stripPrefix(p.clanName))}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── Season Results (manual-entry standings, incl. team logos) ──── */}
      {hasSeasons && (
        <section id="results" style={{ background: 'var(--bg-2)' }}>
          <div className="wrap">
            <div className="eyebrow"><span className="tick"></span>Season by season</div>
            <h2 className="sec-title">Season Results</h2>
            <div style={{ marginTop: 22, display: 'grid', gap: 32 }}>
              {page.seasons!.map((season, si) => (
                <div key={si}>
                  <h3 style={{ fontSize: 20, fontWeight: 800, marginBottom: 16 }}>{season.title}</h3>
                  <div style={{ display: 'grid', gap: 20 }}>
                    {season.divisions.map((division, di) => {
                      const teams = [...division.teams].sort(
                        (a, b) => (a.place ?? Number.MAX_SAFE_INTEGER) - (b.place ?? Number.MAX_SAFE_INTEGER)
                      );
                      const podium = teams.filter(t => !!t.place && t.place >= 1 && t.place <= 3);
                      return (
                        <div key={di} className="plate pad">
                          <div className="eyebrow" style={{ marginBottom: 14 }}><span className="tick"></span>{division.tier}</div>

                          {podium.length > 0 && (
                            <div className="podium-row" style={{ justifyContent: 'center', marginBottom: 20, paddingBottom: 20, borderBottom: '1px solid var(--line)' }}>
                              {podium.map(team => (
                                <div key={team.clanName} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, padding: '0 10px' }}>
                                  <span className="medal" style={{ fontSize: 24 }}>{PLACE_MEDALS[team.place!] ?? `${team.place}.`}</span>
                                  {team.logoUrl && (
                                    <img src={team.logoUrl} alt="" style={{ height: 40, width: 40, objectFit: 'contain', borderRadius: 4 }} />
                                  )}
                                  <span style={{ fontWeight: 700, fontSize: 14 }}>{clanLabelFrom(team.displayName, team.clanName)}</span>
                                  {typeof team.score === 'number' && (
                                    <span className="mono" style={{ fontSize: 12, color: 'var(--accent)' }}>{team.score.toLocaleString()}</span>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}

                          <div style={{ overflowX: 'auto' }}>
                            <table className="standings-table">
                              <thead>
                                <tr>
                                  <th style={{ textAlign: 'center', width: 40 }}>#</th>
                                  <th>Team</th>
                                  <th style={{ textAlign: 'right' }}>Score</th>
                                </tr>
                              </thead>
                              <tbody>
                                {teams.map((team, idx) => (
                                  <tr key={`${team.clanName}-${idx}`}>
                                    <td className="num">{team.place ?? idx + 1}</td>
                                    <td>
                                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                                        {team.logoUrl && (
                                          <img src={team.logoUrl} alt="" loading="lazy" style={{ height: 20, width: 20, objectFit: 'contain', borderRadius: 4 }} />
                                        )}
                                        {clanLabelFrom(team.displayName, team.clanName)}
                                      </span>
                                    </td>
                                    <td className="score">{typeof team.score === 'number' ? team.score.toLocaleString() : '–'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── Hall of Champions ───────────────────────────────────────────── */}
      {hasHallOfChampions && (
        <section id="honours">
          <div className="wrap">
            <div className="eyebrow"><span className="tick"></span>All-time greats</div>
            <h2 className="sec-title">Hall of Champions</h2>
            <div className="plate pad" style={{ marginTop: 22 }}>
              <ol style={{ display: 'grid', gap: 14 }}>
                {hallOfChampions.map((entry, idx) => (
                  <li key={entry.clanName} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <span className="medal">{PLACE_MEDALS[idx + 1] ?? `${idx + 1}.`}</span>
                      <span style={{ fontWeight: 700 }}>{displayClanName(stripPrefix(entry.clanName))}</span>
                    </span>
                    <span className="mono" style={{ fontSize: 12.5, color: 'var(--ink-dim)' }}>
                      {entry.wins} {entry.wins === 1 ? 'win' : 'wins'} ·{' '}
                      {entry.podiums} {entry.podiums === 1 ? 'podium' : 'podiums'}
                    </span>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </section>
      )}

      {/* ── Testimonials ─────────────────────────────────────────────────── */}
      {hasTestimonials && (
        <section id="testimonials" style={{ background: 'var(--bg-2)' }}>
          <div className="wrap">
            <div className="eyebrow"><span className="tick"></span>What people say</div>
            <h2 className="sec-title">Testimonials</h2>
            <div className="grid g-2" style={{ marginTop: 22 }}>
              {page.testimonials!.map((t, idx) => (
                <div key={idx} className="plate pad">
                  <p className="lead" style={{ fontStyle: 'italic' }}>&ldquo;{t.quote}&rdquo;</p>
                  <p style={{ marginTop: 12, fontWeight: 700 }}>
                    {t.author}
                    {t.role && <span style={{ fontWeight: 400, color: 'var(--ink-faint)' }}> · {t.role}</span>}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── FAQ ─────────────────────────────────────────────────────────── */}
      {hasFaq && (
        <section id="faq">
          <div className="wrap">
            <div className="eyebrow"><span className="tick"></span>Good to know</div>
            <h2 className="sec-title">FAQ</h2>
            <div style={{ marginTop: 22 }}>
              {page.faq!.map((item, idx) => (
                <div key={idx}>
                  <p className="faq-q">{item.q}</p>
                  <p className="faq-a" style={{ whiteSpace: 'pre-line' }}>{item.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── Staff ───────────────────────────────────────────────────────── */}
      {hasStaff && (
        <section id="staff" style={{ background: 'var(--bg-2)' }}>
          <div className="wrap">
            <div className="eyebrow"><span className="tick"></span>Behind the scenes</div>
            <h2 className="sec-title">Staff</h2>
            <div className="grid g-3" style={{ marginTop: 22 }}>
              {page.staff!.map((member, idx) => (
                <div key={idx} className="plate pad person">
                  <div className="row">
                    <div className="avatar">{initials(member.name)}</div>
                    <div>
                      <div className="who">{member.name}</div>
                      {member.role && (
                        <div className="mono" style={{ fontSize: 11, letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--ink-faint)' }}>
                          {member.role}
                        </div>
                      )}
                    </div>
                  </div>
                  {member.discord && (
                    <div className="mono" style={{ marginTop: 10, fontSize: 12, color: 'var(--ink-dim)' }}>@{member.discord}</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── Partners ────────────────────────────────────────────────────── */}
      {hasPartners && (
        <section id="partners">
          <div className="wrap">
            <div className="eyebrow"><span className="tick"></span>Backed by</div>
            <h2 className="sec-title">Partners</h2>
            <div className="sponsor-wall" style={{ marginTop: 22 }}>
              {page.partners!.map((partner, idx) => {
                const inner = partner.logoAssetUrl
                  ? <img src={partner.logoAssetUrl} alt={partner.name} />
                  : <span>{partner.name}</span>;
                return (
                  <div key={idx} className="sponsor">
                    {partner.url
                      ? <a href={partner.url} target="_blank" rel="noopener noreferrer">{inner}</a>
                      : inner}
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* ── Contact ─────────────────────────────────────────────────────── */}
      {hasContact && (
        <section id="contact" style={{ background: 'var(--bg-2)' }}>
          <div className="wrap">
            <div className="eyebrow"><span className="tick"></span>Get in touch</div>
            <h2 className="sec-title">Contact</h2>
            <div className="plate pad" style={{ marginTop: 22, maxWidth: 480 }}>
              {contact.note && (
                <p className="lead" style={{ whiteSpace: 'pre-line', marginBottom: 18 }}>{contact.note}</p>
              )}
              <div className="kv">
                {contact.email && (
                  <>
                    <div className="k">Email</div>
                    <div><a href={`mailto:${contact.email}`}>{contact.email}</a></div>
                  </>
                )}
                {contact.website && (
                  <>
                    <div className="k">Website</div>
                    <div><a href={contact.website} target="_blank" rel="noopener noreferrer">{contact.website}</a></div>
                  </>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ── Code of Conduct ─────────────────────────────────────────────── */}
      {/* Links to the org's own rules content inline for now (no standalone
          /org/:slug/rules route exists yet, unlike CompRules for comps) — a
          data-driven CoC page of its own is a separate follow-up. */}
      {hasRules && (
        <section id="coc">
          <div className="wrap">
            <div className="eyebrow"><span className="tick"></span>Play fair, play hard</div>
            <h2 className="sec-title">Code of Conduct</h2>
            <p className="lead" style={{ marginTop: 10 }}>
              Every member and team is expected to follow the organisation's code of conduct below.
            </p>
            <div className="plate pad" style={{ marginTop: 22 }}>
              <MarkdownLite text={page.rules!} />
            </div>
            <div className="cta-row" style={{ marginTop: 22 }}>
              <CopyLinkButton label="Copy page link" />
            </div>
          </div>
        </section>
      )}

      {/* ── Footer ──────────────────────────────────────────────────────── */}
      <footer>
        <div className="wrap foot">
          <div className="brand">
            {orgLogoUrl && <img src={orgLogoUrl} alt={org.name} style={{ height: 32 }} />}
            <div className="name">{org.name}</div>
          </div>
          <div className="copy">Powered by GG&nbsp;Anvil · public org page</div>
        </div>
      </footer>
    </div>
  );
}
